import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type {
  ConnectorHealthStatus,
  ConnectorConfig,
  LiveHealthResult,
} from "../../api/types";
import { S } from "../../styles";
import { LoadingState, ErrorState } from "../../components/QueryStates";
import { th, td, tableStyle } from "./adminStyles";

const STATUS_COLORS: Record<ConnectorHealthStatus, { bg: string; fg: string }> =
  {
    healthy: { bg: "#dcfce7", fg: "#15803d" },
    degraded: { bg: "#fef3c7", fg: "#b45309" },
    down: { bg: "#fee2e2", fg: "#b91c1c" },
    unknown: { bg: "#eef1f6", fg: "#475569" },
  };

function statusBadge(status: ConnectorHealthStatus): React.CSSProperties {
  const c = STATUS_COLORS[status] ?? STATUS_COLORS.unknown;
  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "capitalize",
    background: c.bg,
    color: c.fg,
  };
}

interface ConfigForm {
  connector_name: string;
  connector_type: string;
  base_url: string;
  is_enabled: boolean;
  config_json: string;
}

const EMPTY_CONFIG: ConfigForm = {
  connector_name: "",
  connector_type: "erp",
  base_url: "",
  is_enabled: true,
  config_json: "{}",
};

export function ConnectorHealthPanel() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [liveResults, setLiveResults] = useState<
    Record<string, LiveHealthResult>
  >({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<ConfigForm>({ ...EMPTY_CONFIG });
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ConfigForm>({ ...EMPTY_CONFIG });

  const healthQuery = useQuery({
    queryKey: ["admin-connectors-health"],
    queryFn: () => api.listConnectors(),
    refetchInterval: 15000,
  });
  const configsQuery = useQuery({
    queryKey: ["admin-connector-configs"],
    queryFn: () => api.listConnectorConfigs(),
  });

  const connectors = healthQuery.data ?? [];
  const configs = configsQuery.data ?? [];

  const invalidateConfigs = () =>
    void qc.invalidateQueries({ queryKey: ["admin-connector-configs"] });

  const checkMutation = useMutation({
    mutationFn: (name: string) => api.checkConnectorHealth(name),
    onSuccess: (r) => {
      setLiveResults((prev) => ({ ...prev, [r.connector_name]: r }));
      void qc.invalidateQueries({ queryKey: ["admin-connectors-health"] });
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const createMutation = useMutation({
    mutationFn: (form: ConfigForm) => {
      const parsed = parseJsonOrThrow(form.config_json);
      return api.createConnectorConfig({
        connector_name: form.connector_name.trim(),
        connector_type: form.connector_type.trim(),
        base_url: form.base_url.trim() || null,
        is_enabled: form.is_enabled,
        config_json: parsed,
      });
    },
    onSuccess: (c) => {
      setMsg(`✅ Saved config for ${c.connector_name}`);
      setCreateForm({ ...EMPTY_CONFIG });
      setShowCreate(false);
      invalidateConfigs();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ name, form }: { name: string; form: ConfigForm }) => {
      const parsed = parseJsonOrThrow(form.config_json);
      return api.updateConnectorConfig(name, {
        base_url: form.base_url.trim() || null,
        is_enabled: form.is_enabled,
        config_json: parsed,
      });
    },
    onSuccess: (c) => {
      setMsg(`✅ Updated ${c.connector_name}`);
      setEditingName(null);
      invalidateConfigs();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => api.deleteConnectorConfig(name),
    onSuccess: () => {
      setMsg("✅ Config removed");
      invalidateConfigs();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const startEdit = (c: ConnectorConfig) => {
    setEditingName(c.connector_name);
    setEditForm({
      connector_name: c.connector_name,
      connector_type: c.connector_type,
      base_url: c.base_url ?? "",
      is_enabled: c.is_enabled,
      config_json: JSON.stringify(c.config_json ?? {}, null, 2),
    });
  };

  return (
    <div>
      <h2 style={{ ...S.h2, marginBottom: 4 }}>🔌 Connector Health</h2>
      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16 }}>
        Live status and throughput for every ERP, supplier, and channel
        connector. Auto-refreshes every 15 seconds.
      </p>

      {msg && (
        <div style={{ ...S.card, background: "#eef2ff", marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {/* ── Health dashboard ─────────────────────────────────────────── */}
      {healthQuery.isLoading ? (
        <LoadingState label="Loading connector health…" />
      ) : healthQuery.error ? (
        <ErrorState error={healthQuery.error} />
      ) : (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Connector</th>
                <th style={th}>Type</th>
                <th style={th}>Status</th>
                <th style={th}>Calls 24h</th>
                <th style={th}>Errors 24h</th>
                <th style={th}>Avg latency</th>
                <th style={th}>Last success</th>
                <th style={{ ...th, textAlign: "right" }}>Live check</th>
              </tr>
            </thead>
            <tbody>
              {connectors.map((c) => {
                const live = liveResults[c.connector_name];
                return (
                  <tr key={c.connector_name}>
                    <td style={td}>
                      <span style={{ fontWeight: 600 }}>
                        {c.connector_name}
                      </span>
                      {!c.is_enabled && (
                        <span style={{ ...S.muted, marginLeft: 6 }}>
                          (disabled)
                        </span>
                      )}
                    </td>
                    <td style={td}>{c.connector_type}</td>
                    <td style={td}>
                      <span style={statusBadge(c.status)}>{c.status}</span>
                    </td>
                    <td style={td}>{c.calls_24h}</td>
                    <td style={td}>
                      {c.errors_24h}
                      {c.calls_24h > 0 && (
                        <span style={S.muted}>
                          {" "}
                          ({(c.error_rate_24h * 100).toFixed(1)}%)
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {c.avg_latency_ms_24h != null
                        ? `${c.avg_latency_ms_24h} ms`
                        : "—"}
                    </td>
                    <td style={td}>
                      {c.last_success_at
                        ? new Date(c.last_success_at).toLocaleString()
                        : "—"}
                    </td>
                    <td
                      style={{
                        ...td,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <button
                        style={S.btnGhost}
                        disabled={checkMutation.isPending}
                        onClick={() => checkMutation.mutate(c.connector_name)}
                      >
                        Check now
                      </button>
                      {live && (
                        <div
                          style={{
                            ...S.muted,
                            marginTop: 4,
                            color: live.reachable ? "#15803d" : "#b91c1c",
                          }}
                        >
                          {live.reachable ? "✓ reachable" : "✗ unreachable"}
                          {live.latency_ms != null
                            ? ` · ${live.latency_ms} ms`
                            : ""}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Configuration / credentials ──────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "24px 0 8px",
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>Connector configuration</h2>
        <button style={S.btn} onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? "Cancel" : "+ Add config"}
        </button>
      </div>
      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16 }}>
        Per-tenant base URLs and credentials. Secrets are stored server-side and
        never returned in cleartext to other tenants.
      </p>

      {showCreate && (
        <div style={S.card}>
          <h3 style={S.h3}>Add connector config</h3>
          <div style={S.grid2}>
            <div>
              <label style={S.label}>Connector name</label>
              <select
                style={S.select}
                value={createForm.connector_name}
                onChange={(e) => {
                  const name = e.target.value;
                  const known = connectors.find(
                    (c) => c.connector_name === name,
                  );
                  setCreateForm({
                    ...createForm,
                    connector_name: name,
                    connector_type:
                      known?.connector_type ?? createForm.connector_type,
                  });
                }}
              >
                <option value="">Select a connector…</option>
                {connectors.map((c) => (
                  <option key={c.connector_name} value={c.connector_name}>
                    {c.connector_name} ({c.connector_type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Type</label>
              <input
                style={S.input}
                value={createForm.connector_type}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    connector_type: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <ConfigFields form={createForm} onChange={setCreateForm} />
          <div style={{ marginTop: 12 }}>
            <button
              style={{
                ...S.btn,
                opacity: createForm.connector_name ? 1 : 0.5,
              }}
              disabled={!createForm.connector_name || createMutation.isPending}
              onClick={() => createMutation.mutate(createForm)}
            >
              {createMutation.isPending ? "Saving…" : "Save config"}
            </button>
          </div>
        </div>
      )}

      {configsQuery.isLoading ? (
        <LoadingState label="Loading configs…" />
      ) : configsQuery.error ? (
        <ErrorState error={configsQuery.error} />
      ) : configs.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>
            No per-tenant connector configs. Connectors fall back to platform
            defaults until you add one here.
          </p>
        </div>
      ) : (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Connector</th>
                <th style={th}>Type</th>
                <th style={th}>Base URL</th>
                <th style={th}>Enabled</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c.id}>
                  <td style={td}>
                    <span style={{ fontWeight: 600 }}>{c.connector_name}</span>
                  </td>
                  <td style={td}>{c.connector_type}</td>
                  <td style={td}>{c.base_url ?? "—"}</td>
                  <td style={td}>
                    <span
                      style={S.statusBadge(c.is_enabled ? "Quoted" : "Expired")}
                    >
                      {c.is_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td
                    style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}
                  >
                    <button style={S.btnGhost} onClick={() => startEdit(c)}>
                      Edit
                    </button>{" "}
                    <button
                      style={{ ...S.btnGhost, color: "#b91c1c" }}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove config for ${c.connector_name}?`,
                          )
                        )
                          deleteMutation.mutate(c.connector_name);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingName && (
        <div style={{ ...S.card, borderColor: "#c7d2fe" }}>
          <h3 style={S.h3}>
            Edit config — <span style={S.muted}>{editingName}</span>
          </h3>
          <ConfigFields form={editForm} onChange={setEditForm} />
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button
              style={S.btn}
              disabled={updateMutation.isPending}
              onClick={() =>
                updateMutation.mutate({ name: editingName, form: editForm })
              }
            >
              {updateMutation.isPending ? "Saving…" : "Save changes"}
            </button>
            <button style={S.btnGhost} onClick={() => setEditingName(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigFields({
  form,
  onChange,
}: {
  form: ConfigForm;
  onChange: (f: ConfigForm) => void;
}) {
  const jsonValid = isJsonValid(form.config_json);
  return (
    <>
      <label style={S.label}>Base URL</label>
      <input
        style={S.input}
        value={form.base_url}
        onChange={(e) => onChange({ ...form, base_url: e.target.value })}
        placeholder="https://connector.internal:8443"
      />
      <label style={S.label}>Credentials / config (JSON)</label>
      <textarea
        style={{
          ...S.input,
          minHeight: 110,
          fontFamily: "var(--font-mono, monospace)",
          borderColor: jsonValid ? "#d7dce6" : "#f87171",
        }}
        value={form.config_json}
        onChange={(e) => onChange({ ...form, config_json: e.target.value })}
        placeholder='{"api_key": "…"}'
      />
      {!jsonValid && <div style={S.err}>Invalid JSON</div>}
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={form.is_enabled}
          onChange={(e) => onChange({ ...form, is_enabled: e.target.checked })}
        />
        Enabled
      </label>
    </>
  );
}

function isJsonValid(raw: string): boolean {
  try {
    const v: unknown = JSON.parse(raw);
    return typeof v === "object" && v !== null && !Array.isArray(v);
  } catch {
    return false;
  }
}

function parseJsonOrThrow(raw: string): Record<string, unknown> {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    throw new Error("Config JSON is not valid JSON");
  }
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new Error("Config JSON must be a JSON object");
  }
  return v as Record<string, unknown>;
}
